import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Chip,
  FormControl, InputLabel, Select, MenuItem, Tab, Tabs,
  Card, CardContent, Divider, Alert, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, AttachMoney, Calculate, Print, Refresh, Visibility } from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const todayStr = () => new Date().toISOString().slice(0, 10);

const Finance = () => {
  const [tabValue, setTabValue] = useState(0);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openExpenseDialog, setOpenExpenseDialog] = useState(false);
  const [openPaymentDetailDialog, setOpenPaymentDetailDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedPaymentDetail, setSelectedPaymentDetail] = useState(null);

  const emptyPaymentForm = () => ({
    payment_type: 'received',
    settlement_type: 'full',
    amount: 0,
    payment_method: 'cash',
    reference_number: '',
    notes: '',
    customer: '',
    sales_order: '',
    supplier: '',
    purchase_order: '',
    payment_date: todayStr(),
  });

  const emptyExpenseForm = () => ({
    category: 'others',
    amount: 0,
    description: '',
    vendor: '',
    bill_number: '',
    tonnage: '',
    rate_per_ton: '',
    expense_date: todayStr(),
  });

  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm());
  const [expenseForm, setExpenseForm] = useState(emptyExpenseForm());

  const [totalReceived, setTotalReceived] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);

  const SETTLEMENT_LABELS = {
    full: 'Full Payment',
    partial: 'Partial Payment',
    final: 'Final Payment',
    advance: 'Advance Payment'
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchPayments(),
        fetchExpenses(),
        fetchCustomers(),
        fetchSuppliers(),
        fetchSalesOrders(),
        fetchPurchaseOrders()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await api.get('/finance/payments/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setPayments(data);

      let received = 0, paid = 0;
      data.forEach(p => {
        if (p.payment_type === 'received') received += parseFloat(p.amount);
        else paid += parseFloat(p.amount);
      });
      setTotalReceived(received);
      setTotalPaid(paid);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/sales/customers/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/procurement/suppliers/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchSalesOrders = async () => {
    try {
      const response = await api.get('/sales/orders/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setSalesOrders(data);
    } catch (error) {
      console.error('Error fetching sales orders:', error);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await api.get('/procurement/orders/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setPurchaseOrders(data);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/finance/expenses/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setExpenses(data);

      let total = 0;
      data.forEach(e => total += parseFloat(e.amount));
      setTotalExpenses(total);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    }
  };

  const handleCustomerChange = async (customerId) => {
    setPaymentForm({ ...paymentForm, customer: customerId, sales_order: '' });
    setSelectedOrderDetails(null);
    setPaymentHistory([]);

    if (customerId) {
      try {
        const response = await api.get('/sales/orders/');
        let data = [];
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data?.results) {
          data = response.data.results;
        }
        const filteredOrders = data.filter(o =>
          o.customer?.id === parseInt(customerId) &&
          o.status !== 'cancelled'
        );
        setSalesOrders(filteredOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
      }
    }
  };

  const handleSupplierChange = async (supplierId) => {
    setPaymentForm({ ...paymentForm, supplier: supplierId, purchase_order: '' });
    setSelectedOrderDetails(null);
    setPaymentHistory([]);

    if (supplierId) {
      try {
        const response = await api.get('/procurement/orders/');
        let data = [];
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data?.results) {
          data = response.data.results;
        }
        const filteredOrders = data.filter(o =>
          parseInt(o.supplier) === parseInt(supplierId) &&
          o.status !== 'cancelled'
        );
        setPurchaseOrders(filteredOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
      }
    }
  };

  const handleOrderSelect = async (orderId, type) => {
    setPaymentForm(prev => ({
      ...prev,
      [type === 'sales' ? 'sales_order' : 'purchase_order']: orderId
    }));

    if (orderId) {
      setLoading(true);
      try {
        let order;
        if (type === 'sales') {
          const response = await api.get(`/sales/orders/${orderId}/`);
          order = response.data;
        } else {
          const response = await api.get(`/procurement/orders/${orderId}/`);
          order = response.data;
        }

        if (order) {
          const amountPaid = parseFloat(order.amount_paid || 0);
          const netAmount = parseFloat(order.net_amount || order.total_amount || 0);
          const dueAmount = netAmount - amountPaid;

          setSelectedOrderDetails({
            order_no: type === 'sales' ? order.so_number : order.po_number,
            total_amount: netAmount,
            amount_paid: amountPaid,
            due_amount: dueAmount,
            order_type: type,
            order_value: parseFloat(order.total_amount || 0),
            tax_amount: parseFloat(order.tax_amount || order.total_tax || 0),
            gst_rate_details: order.gst_rate_details || null,
            cgst_amount: parseFloat(order.cgst_amount || 0),
            sgst_amount: parseFloat(order.sgst_amount || 0),
            igst_amount: parseFloat(order.igst_amount || 0),
            order_id: order.id
          });

          const params = type === 'sales'
            ? { sales_order: orderId }
            : { purchase_order: orderId };

          const historyRes = await api.get('/finance/payments/', { params });

          let historyData = [];
          if (Array.isArray(historyRes.data)) {
            historyData = historyRes.data;
          } else if (historyRes.data?.results) {
            historyData = historyRes.data.results;
          }

          setPaymentHistory(historyData);

          const settlementType = paymentForm.settlement_type || 'full';
          let defaultAmount = 0;
          if (settlementType === 'full' || settlementType === 'final') {
            defaultAmount = dueAmount;
          } else if (settlementType === 'partial') {
            defaultAmount = dueAmount > 0 ? dueAmount / 2 : 0;
          } else if (settlementType === 'advance') {
            defaultAmount = 0;
          }
          setPaymentForm(prev => ({
            ...prev,
            amount: defaultAmount > 0 ? defaultAmount : 0
          }));
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        toast.error('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    }
  };

  const viewPaymentDetail = (payment) => {
    const fetchOrderDetails = async () => {
      try {
        let orderDetail = null;
        let orderId = null;
        let isSales = false;

        if (payment.sales_order) {
          isSales = true;
          if (typeof payment.sales_order === 'object' && payment.sales_order.id) {
            orderId = payment.sales_order.id;
          } else if (typeof payment.sales_order === 'number') {
            orderId = payment.sales_order;
          } else {
            orderId = parseInt(payment.sales_order);
          }

          if (orderId && !isNaN(orderId)) {
            const response = await api.get(`/sales/orders/${orderId}/`);
            orderDetail = response.data;
          }
        } else if (payment.purchase_order) {
          isSales = false;
          if (typeof payment.purchase_order === 'object' && payment.purchase_order.id) {
            orderId = payment.purchase_order.id;
          } else if (typeof payment.purchase_order === 'number') {
            orderId = payment.purchase_order;
          } else {
            orderId = parseInt(payment.purchase_order);
          }

          if (orderId && !isNaN(orderId)) {
            const response = await api.get(`/procurement/orders/${orderId}/`);
            orderDetail = response.data;
          }
        }

        if (orderDetail) {
          const allPaymentsRes = await api.get('/finance/payments/', {
            params: isSales
              ? { sales_order: orderId }
              : { purchase_order: orderId }
          });

          let allPayments = [];
          if (Array.isArray(allPaymentsRes.data)) {
            allPayments = allPaymentsRes.data;
          } else if (allPaymentsRes.data?.results) {
            allPayments = allPaymentsRes.data.results;
          }

          allPayments.sort((a, b) => {
            const dateA = new Date(a.payment_date);
            const dateB = new Date(b.payment_date);
            if (dateA < dateB) return -1;
            if (dateA > dateB) return 1;
            return a.id - b.id;
          });

          const paymentIndex = allPayments.findIndex(p => p.id === payment.id);

          let cumulativePaid = 0;
          for (let i = 0; i <= paymentIndex && i < allPayments.length; i++) {
            cumulativePaid += parseFloat(allPayments[i].amount || 0);
          }

          const netAmount = parseFloat(orderDetail.net_amount || 0);
          const runningDue = netAmount - cumulativePaid;

          setSelectedPaymentDetail({
            ...payment,
            order_value: parseFloat(orderDetail.total_amount || 0),
            tax_amount: parseFloat(orderDetail.tax_amount || orderDetail.total_tax || 0),
            gst_rate_details: orderDetail.gst_rate_details || null,
            cgst_amount: parseFloat(orderDetail.cgst_amount || 0),
            sgst_amount: parseFloat(orderDetail.sgst_amount || 0),
            igst_amount: parseFloat(orderDetail.igst_amount || 0),
            net_amount: netAmount,
            cumulative_amount_paid: cumulativePaid,
            running_due_amount: runningDue,
            order_status: orderDetail.status || 'N/A',
            is_sales_order: isSales,
            order_no: isSales ? orderDetail.so_number : orderDetail.po_number,
            customer: orderDetail.customer,
            supplier: orderDetail.supplier
          });
          setOpenPaymentDetailDialog(true);
        } else {
          setSelectedPaymentDetail({
            ...payment,
            order_value: 0,
            tax_amount: 0,
            net_amount: 0,
            cumulative_amount_paid: parseFloat(payment.amount || 0),
            running_due_amount: 0,
            order_status: 'N/A',
            is_sales_order: !!payment.sales_order
          });
          setOpenPaymentDetailDialog(true);
          toast.info('Order details not available for this payment');
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        setSelectedPaymentDetail({
          ...payment,
          order_value: 0,
          tax_amount: 0,
          net_amount: 0,
          cumulative_amount_paid: parseFloat(payment.amount || 0),
          running_due_amount: 0,
          order_status: 'N/A',
          is_sales_order: !!payment.sales_order
        });
        setOpenPaymentDetailDialog(true);
        toast.error('Could not fetch complete order details');
      }
    };

    fetchOrderDetails();
  };

  const handleSettlementTypeChange = (settlementType) => {
    setPaymentForm(prev => ({
      ...prev,
      settlement_type: settlementType
    }));

    if (selectedOrderDetails) {
      let defaultAmount = 0;
      if (settlementType === 'full' || settlementType === 'final') {
        defaultAmount = selectedOrderDetails.due_amount;
      } else if (settlementType === 'partial') {
        defaultAmount = selectedOrderDetails.due_amount > 0 ? selectedOrderDetails.due_amount / 2 : 0;
      } else if (settlementType === 'advance') {
        defaultAmount = 0;
      }
      setPaymentForm(prev => ({
        ...prev,
        amount: defaultAmount > 0 ? defaultAmount : 0
      }));
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      if (paymentForm.payment_type === 'received' && !paymentForm.customer) {
        toast.error('Please select a customer');
        return;
      }
      if (paymentForm.payment_type === 'paid' && !paymentForm.supplier) {
        toast.error('Please select a supplier');
        return;
      }
      if (parseFloat(paymentForm.amount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      if (!paymentForm.payment_date) {
        toast.error('Please select a payment date');
        return;
      }

      const submitData = {
        payment_type: paymentForm.payment_type,
        settlement_type: paymentForm.settlement_type,
        amount: parseFloat(paymentForm.amount) || 0,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || '',
        notes: paymentForm.notes || '',
        payment_date: paymentForm.payment_date,
      };

      if (paymentForm.payment_type === 'received') {
        if (paymentForm.customer) {
          submitData.customer_id = parseInt(paymentForm.customer);
        }
        if (paymentForm.sales_order) {
          submitData.sales_order_id = parseInt(paymentForm.sales_order);
        }
      } else {
        if (paymentForm.supplier) {
          submitData.supplier_id = parseInt(paymentForm.supplier);
        }
        if (paymentForm.purchase_order) {
          submitData.purchase_order_id = parseInt(paymentForm.purchase_order);
        }
      }

      if (editingPayment) {
        await api.put(`/finance/payments/${editingPayment.id}/`, submitData);
        toast.success('Payment updated successfully');
      } else {
        await api.post('/finance/payments/', submitData);
        toast.success('Payment recorded successfully');
      }

      setOpenPaymentDialog(false);
      setEditingPayment(null);
      setPaymentForm(emptyPaymentForm());
      setSelectedOrderDetails(null);
      setPaymentHistory([]);

      await fetchPayments();
    } catch (error) {
      console.error('Error saving payment:', error);
      if (error.response) {
        toast.error(`Failed to save: ${JSON.stringify(error.response.data)}`);
      } else {
        toast.error('Failed to save payment');
      }
    }
  };

  const openEditPaymentDialog = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      payment_type: payment.payment_type || 'received',
      settlement_type: payment.settlement_type || 'full',
      amount: parseFloat(payment.amount) || 0,
      payment_method: payment.payment_method || 'cash',
      reference_number: payment.reference_number || '',
      notes: payment.notes || '',
      customer: payment.customer?.id || '',
      sales_order: payment.sales_order?.id || '',
      supplier: payment.supplier?.id || '',
      purchase_order: payment.purchase_order?.id || '',
      payment_date: payment.payment_date
        ? payment.payment_date.slice(0, 10)
        : todayStr(),
    });

    if (payment.sales_order) {
      handleOrderSelect(payment.sales_order.id, 'sales');
    } else if (payment.purchase_order) {
      handleOrderSelect(payment.purchase_order.id, 'purchase');
    }

    setOpenPaymentDialog(true);
  };

  const handlePaymentDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await api.delete(`/finance/payments/${id}/`);
        toast.success('Payment deleted successfully');
        fetchPayments();
      } catch (error) {
        toast.error('Failed to delete payment');
      }
    }
  };

  const isHandlingCategory = (category) => {
    return category === 'loading' || category === 'unloading';
  };

  const calculateHandlingCost = () => {
    const tonnage = parseFloat(expenseForm.tonnage) || 0;
    const ratePerTon = parseFloat(expenseForm.rate_per_ton) || 0;
    return tonnage * ratePerTon;
  };

  useEffect(() => {
    if (isHandlingCategory(expenseForm.category)) {
      const total = calculateHandlingCost();
      if (parseFloat(expenseForm.amount) !== total) {
        setExpenseForm(prev => ({
          ...prev,
          amount: total
        }));
      }
    }
  }, [expenseForm.tonnage, expenseForm.rate_per_ton, expenseForm.category]);

  const handleExpenseSubmit = async () => {
    try {
      if (!expenseForm.description || expenseForm.description.trim() === '') {
        toast.error('Please enter a description');
        return;
      }
      if (!expenseForm.expense_date) {
        toast.error('Please select an expense date');
        return;
      }

      const submitData = {
        category: expenseForm.category,
        description: expenseForm.description.trim(),
        vendor: expenseForm.vendor || '',
        bill_number: expenseForm.bill_number || '',
        expense_date: expenseForm.expense_date,
      };

      if (expenseForm.category === 'loading' || expenseForm.category === 'unloading') {
        const tonnage = parseFloat(expenseForm.tonnage) || 0;
        const ratePerTon = parseFloat(expenseForm.rate_per_ton) || 0;

        if (tonnage <= 0) {
          toast.error('Please enter valid tonnage');
          return;
        }
        if (ratePerTon <= 0) {
          toast.error('Please enter valid rate per ton');
          return;
        }

        submitData.tonnage = tonnage;
        submitData.rate_per_ton = ratePerTon;
        submitData.amount = tonnage * ratePerTon;
      } else {
        const amount = parseFloat(expenseForm.amount) || 0;
        if (amount <= 0) {
          toast.error('Please enter a valid amount');
          return;
        }
        submitData.amount = amount;
      }

      if (editingExpense) {
        await api.put(`/finance/expenses/${editingExpense.id}/`, submitData);
        toast.success('Expense updated successfully');
      } else {
        await api.post('/finance/expenses/', submitData);
        toast.success('Expense recorded successfully');
      }

      setOpenExpenseDialog(false);
      setEditingExpense(null);
      setExpenseForm(emptyExpenseForm());
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Failed to record expense');
    }
  };

  const handleExpenseDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await api.delete(`/finance/expenses/${id}/`);
        toast.success('Expense deleted successfully');
        fetchExpenses();
      } catch (error) {
        toast.error('Failed to delete expense');
      }
    }
  };

  const openEditExpenseDialog = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      category: expense.category || 'others',
      amount: parseFloat(expense.amount) || 0,
      description: expense.description || '',
      vendor: expense.vendor || '',
      bill_number: expense.bill_number || '',
      tonnage: expense.tonnage || '',
      rate_per_ton: expense.rate_per_ton || '',
      expense_date: expense.expense_date
        ? expense.expense_date.slice(0, 10)
        : todayStr(),
    });
    setOpenExpenseDialog(true);
  };

  const handleCategoryChange = (category) => {
    setExpenseForm({
      ...expenseForm,
      category: category,
      tonnage: isHandlingCategory(category) ? expenseForm.tonnage : '',
      rate_per_ton: isHandlingCategory(category) ? expenseForm.rate_per_ton : '',
      amount: isHandlingCategory(category) ? calculateHandlingCost() : 0
    });
  };

  const printPaymentReceipt = (payment) => {
    const printWindow = window.open('', '_blank', 'width=650,height=700');
    const partyName = payment.customer?.name || payment.supplier?.name || 'N/A';
    const order = payment.sales_order || payment.purchase_order;
    const orderNo = order?.so_number || order?.po_number || 'N/A';
    const settlementLabel = SETTLEMENT_LABELS[payment.settlement_type] || payment.settlement_type;

    const isSales = !!payment.sales_order;
    const orderValue = parseFloat(payment.order_value || 0);
    const netAmount = parseFloat(payment.net_amount || 0);
    const cumulativePaid = parseFloat(payment.cumulative_amount_paid || 0);
    const runningDue = parseFloat(payment.running_due_amount || 0);
    const thisPayment = parseFloat(payment.amount || 0);
    const taxAmount = parseFloat(payment.tax_amount || 0);
    const gstRate = payment.gst_rate_details;
    const cgst = parseFloat(payment.cgst_amount || 0);
    const sgst = parseFloat(payment.sgst_amount || 0);
    const igst = parseFloat(payment.igst_amount || 0);
    const hasGST = gstRate || cgst > 0 || sgst > 0 || igst > 0;
    const paymentDateTime = new Date(payment.payment_date);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment Receipt</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { text-align: center; border-bottom: 2px solid #2E7D32; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { color: #2E7D32; margin: 0; font-size: 24px; }
          .header p { margin: 5px 0; color: #666; font-size: 14px; }
          .receipt { max-width: 650px; margin: 0 auto; }
          .section { margin: 15px 0; padding: 15px; border-radius: 5px; }
          .section-payment { background: #f5f5f5; }
          .section-order { background: #e8f5e9; }
          .section-balance { background: #e3f2fd; }
          .section-gst { background: #fff3e0; }
          .details table { width: 100%; }
          .details td { padding: 6px 5px; border-bottom: 1px solid #eee; }
          .details .label { font-weight: bold; width: 140px; }
          .amount { font-size: 24px; color: #2E7D32; font-weight: bold; text-align: center; padding: 15px; background: #f5f5f5; border-radius: 5px; margin: 20px 0; }
          .footer { margin-top: 30px; text-align: center; border-top: 1px solid #ddd; padding-top: 20px; color: #666; font-size: 12px; }
          .no-print { display: none; }
          @media print { .no-print { display: none; } body { margin: 20px; } }
          .timestamp { color: #666; font-size: 12px; }
          .due-positive { color: #d32f2f; font-weight: bold; }
          .due-zero { color: #2e7d32; font-weight: bold; }
          .section-title { margin-top: 0; margin-bottom: 10px; }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1>Sri Shakthi Traders</h1>
            <p>Cashew Shell Oil Extraction Unit</p>
            <p>GST: 29ABCDE1234F1Z5 | Phone: +91 9876543210</p>
          </div>
          <h2 style="text-align:center;">${payment.payment_type === 'received' ? 'Payment Receipt' : 'Payment Voucher'}</h2>

          <div class="section section-payment">
            <h3 class="section-title" style="color:#1976D2;">Payment Details</h3>
            <div class="details">
              <table>
                <tr><td class="label">${payment.payment_type === 'received' ? 'Customer:' : 'Supplier:'}</td><td><strong>${partyName}</strong></td></tr>
                <tr><td class="label">Payment Type:</td><td>${payment.payment_type.toUpperCase()}</td></tr>
                <tr><td class="label">Settlement Type:</td><td><strong>${settlementLabel}</strong></td></tr>
                <tr><td class="label">Amount:</td><td><strong style="color:#2E7D32;font-size:20px;">₹${thisPayment.toFixed(2)}</strong></td></tr>
                <tr><td class="label">Method:</td><td>${payment.payment_method.replace('_', ' ').toUpperCase()}</td></tr>
                <tr><td class="label">Transaction Date:</td><td>${paymentDateTime.toLocaleString('en-IN', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', second: '2-digit'
                })}</td></tr>
                <tr><td class="label">Reference:</td><td>${payment.reference_number || 'N/A'}</td></tr>
                ${payment.notes ? `<tr><td class="label">Notes:</td><td>${payment.notes}</td></tr>` : ''}
              </table>
            </div>
          </div>

          <div class="section section-order">
            <h3 class="section-title" style="color:#2E7D32;">Order Details</h3>
            <div class="details">
              <table>
                <tr><td class="label">Order No:</td><td><strong>${orderNo}</strong></td></tr>
                <tr><td class="label">Order Type:</td><td>${isSales ? 'Sales Order' : 'Purchase Order'}</td></tr>
                <tr><td class="label">Order Status:</td><td>${payment.order_status || 'N/A'}</td></tr>
                <tr><td class="label">Order Value:</td><td>₹${orderValue.toFixed(2)}</td></tr>
                ${taxAmount > 0 ? `<tr><td class="label">Tax Amount:</td><td>₹${taxAmount.toFixed(2)}</td></tr>` : ''}
                <tr><td class="label">Net Amount:</td><td><strong>₹${netAmount.toFixed(2)}</strong></td></tr>
              </table>
            </div>
          </div>

          <div class="section section-balance">
            <h3 class="section-title" style="color:#1976D2;">Balance at the time of this payment</h3>
            <div class="details">
              <table>
                <tr><td class="label">Cumulative Amount Paid:</td><td><strong style="color:#1976D2;">₹${cumulativePaid.toFixed(2)}</strong></td></tr>
                <tr><td class="label">This Payment:</td><td><strong style="color:#2E7D32;">₹${thisPayment.toFixed(2)}</strong></td></tr>
                <tr><td class="label">Due Amount:</td><td><strong class="${runningDue > 0 ? 'due-positive' : 'due-zero'}">₹${runningDue.toFixed(2)}</strong></td></tr>
              </table>
            </div>
          </div>

          ${hasGST ? `
          <div class="section section-gst">
            <h3 class="section-title" style="color:#ED6C02;">GST Details</h3>
            <div class="details">
              <table>
                ${gstRate ? `<tr><td class="label">GST Rate:</td><td>${gstRate.name} (${gstRate.rate}%)</td></tr>` : ''}
                ${cgst > 0 ? `<tr><td class="label">CGST:</td><td>₹${cgst.toFixed(2)}</td></tr>` : ''}
                ${sgst > 0 ? `<tr><td class="label">SGST:</td><td>₹${sgst.toFixed(2)}</td></tr>` : ''}
                ${igst > 0 ? `<tr><td class="label">IGST:</td><td>₹${igst.toFixed(2)}</td></tr>` : ''}
                ${taxAmount > 0 ? `<tr><td class="label">Total Tax:</td><td><strong>₹${taxAmount.toFixed(2)}</strong></td></tr>` : ''}
              </table>
            </div>
          </div>
          ` : ''}

          <div class="amount">₹${thisPayment.toFixed(2)}</div>

          <div class="footer">
            <p>This is a system generated receipt. No signature required.</p>
            <p>Printed on: ${new Date().toLocaleString('en-IN')}</p>
          </div>

          <div style="text-align:center; margin-top:20px;" class="no-print">
            <button onclick="window.print()" style="padding:10px 30px; background:#2E7D32; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px;">Print / Download PDF</button>
            <button onclick="window.close()" style="padding:10px 30px; background:#666; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px; margin-left:10px;">Close</button>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const printExpenseVoucher = (expense) => {
    const printWindow = window.open('', '_blank', 'width=600,height=500');
    const isHandling = expense.category === 'loading' || expense.category === 'unloading';
    const expenseDateTime = new Date(expense.expense_date);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Expense Voucher</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { text-align: center; border-bottom: 2px solid #2E7D32; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { color: #2E7D32; margin: 0; font-size: 24px; }
          .header p { margin: 5px 0; color: #666; font-size: 14px; }
          .voucher { max-width: 550px; margin: 0 auto; }
          .details { margin: 20px 0; }
          .details table { width: 100%; }
          .details td { padding: 8px 5px; border-bottom: 1px solid #eee; }
          .details .label { font-weight: bold; width: 130px; }
          .amount { font-size: 24px; color: #D32F2F; font-weight: bold; text-align: center; padding: 15px; background: #f5f5f5; border-radius: 5px; margin: 20px 0; }
          .footer { margin-top: 30px; text-align: center; border-top: 1px solid #ddd; padding-top: 20px; color: #666; font-size: 12px; }
          .no-print { display: none; }
          @media print { .no-print { display: none; } body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="voucher">
          <div class="header">
            <h1>Sri Shakthi Traders</h1>
            <p>Cashew Shell Oil Extraction Unit</p>
            <p>GST: 29ABCDE1234F1Z5 | Phone: +91 9876543210</p>
          </div>
          <h2 style="text-align:center;">Expense Voucher</h2>
          <div class="details">
            <table>
              <tr><td class="label">Category:</td><td><strong>${getCategoryDisplay(expense.category)}</strong></td></tr>
              <tr><td class="label">Amount:</td><td><strong style="color:#D32F2F;font-size:18px;">₹${parseFloat(expense.amount).toFixed(2)}</strong></td></tr>
              ${isHandling ? `
              <tr><td class="label">Tonnage:</td><td>${expense.tonnage} tons</td></tr>
              <tr><td class="label">Rate/Ton:</td><td>₹${expense.rate_per_ton}</td></tr>
              <tr><td class="label">Calculation:</td><td>${expense.tonnage} tons × ₹${expense.rate_per_ton} = ₹${parseFloat(expense.amount).toFixed(2)}</td></tr>
              ` : ''}
              <tr><td class="label">Description:</td><td>${expense.description}</td></tr>
              <tr><td class="label">Transaction Date:</td><td>${expenseDateTime.toLocaleString('en-IN', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit', second: '2-digit'
              })}</td></tr>
              <tr><td class="label">Vendor:</td><td>${expense.vendor || 'N/A'}</td></tr>
              <tr><td class="label">Bill No:</td><td>${expense.bill_number || 'N/A'}</td></tr>
            </table>
          </div>
          <div class="amount">₹${parseFloat(expense.amount).toFixed(2)}</div>
          <div class="footer">
            <p>This is a system generated voucher. No signature required.</p>
            <p>Printed on: ${new Date().toLocaleString('en-IN')}</p>
          </div>
          <div style="text-align:center; margin-top:20px;" class="no-print">
            <button onclick="window.print()" style="padding:10px 30px; background:#2E7D32; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px;">Print / Download PDF</button>
            <button onclick="window.close()" style="padding:10px 30px; background:#666; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px; margin-left:10px;">Close</button>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const StatCard = ({ title, value, color, icon }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="subtitle2">{title}</Typography>
            <Typography variant="h5" fontWeight="bold" color={color}>₹{value.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ bgcolor: color + '20', borderRadius: '50%', p: 1 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const getCategoryDisplay = (category) => {
    const categories = {
      electricity: 'Electricity Bill',
      diesel: 'Diesel for Vehicles',
      labour: 'Labour Wages',
      driver_salary: 'Driver Salary',
      vehicle_maintenance: 'Vehicle Maintenance',
      machine_maintenance: 'Machine Maintenance',
      machine_parts: 'Machine Spare Parts',
      loading: 'Loading Charges',
      unloading: 'Unloading Charges',
      others: 'Others'
    };
    return categories[category] || category;
  };

  const getCategoryChip = (category) => {
    const colors = {
      electricity: 'warning',
      diesel: 'info',
      labour: 'primary',
      driver_salary: 'primary',
      vehicle_maintenance: 'secondary',
      machine_maintenance: 'secondary',
      machine_parts: 'secondary',
      loading: 'success',
      unloading: 'success',
      others: 'default'
    };
    return <Chip label={getCategoryDisplay(category)} color={colors[category] || 'default'} size="small" />;
  };

  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    const currentTab = tabValue === 0 ? 'Payments' : 'Expenses';
    const data = tabValue === 0 ? payments : expenses;

    let rowsHtml = '';
    if (tabValue === 0) {
      rowsHtml = data.map(p => `
        <tr>
          <td>${p.payment_type.toUpperCase()}</td>
          <td>${p.customer?.name || p.supplier?.name || 'N/A'}</td>
          <td>${p.sales_order?.so_number || p.purchase_order?.po_number || 'N/A'}</td>
          <td>${SETTLEMENT_LABELS[p.settlement_type] || p.settlement_type}</td>
          <td>₹${parseFloat(p.amount).toFixed(2)}</td>
          <td>${p.payment_method?.replace('_', ' ').toUpperCase()}</td>
          <td>${new Date(p.payment_date).toLocaleString('en-IN')}</td>
        </tr>
      `).join('');
    } else {
      rowsHtml = data.map(e => `
        <tr>
          <td>${getCategoryDisplay(e.category)}</td>
          <td>${e.description}</td>
          <td style="text-align:right;">₹${parseFloat(e.amount).toFixed(2)}</td>
          <td style="text-align:center;">${e.tonnage ? `${e.tonnage} tons` : '-'}</td>
          <td style="text-align:right;">${e.rate_per_ton ? `₹${e.rate_per_ton}` : '-'}</td>
          <td>${e.vendor || 'N/A'}</td>
          <td>${new Date(e.expense_date).toLocaleString('en-IN')}</td>
        </tr>
      `).join('');
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Finance Report - ${currentTab}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { text-align: center; border-bottom: 2px solid #2E7D32; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { color: #2E7D32; margin: 0; }
          .header p { margin: 5px 0; color: #666; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #2E7D32; color: white; padding: 10px; text-align: left; }
          td { padding: 10px; border-bottom: 1px solid #ddd; }
          .summary { margin-top: 20px; padding: 20px; background: #f5f5f5; border-radius: 5px; }
          .summary table { width: auto; margin: 0; }
          .summary td { padding: 5px 15px; border: none; }
          .footer { margin-top: 30px; text-align: center; border-top: 1px solid #ddd; padding-top: 20px; color: #666; font-size: 12px; }
          .no-print { display: none; }
          @media print { .no-print { display: none; } body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Sri Shakthi Traders</h1>
          <p>Cashew Shell Oil Extraction Unit</p>
          <p>GST: 29ABCDE1234F1Z5 | Phone: +91 9876543210</p>
          <p>Email: info@srishakthitraders.com</p>
        </div>
        <h2>${currentTab} Report</h2>
        <p><strong>Generated on:</strong> ${new Date().toLocaleString('en-IN')}</p>
        <table>
          <thead>
            <tr>${tabValue === 0 ? `
              <th>Type</th><th>Party</th><th>Order</th><th>Settlement</th><th>Amount</th><th>Method</th><th>Date & Time</th>
            ` : `
              <th>Category</th><th>Description</th><th style="text-align:right;">Amount</th><th style="text-align:center;">Tonnage</th><th style="text-align:right;">Rate/Ton</th><th>Vendor</th><th>Date</th>
            `}</tr>
          </thead>
          <tbody>${rowsHtml || '<tr><td colspan="7" style="text-align:center;">No records found</td></tr>'}</tbody>
        </table>
        <div class="summary">
          <table>
            <tr><td><strong>Total Records:</strong></td><td>${data.length}</td></tr>
            ${tabValue === 0 ? `
              <tr><td><strong>Total Received:</strong></td><td>₹${totalReceived.toFixed(2)}</td></tr>
              <tr><td><strong>Total Paid:</strong></td><td>₹${totalPaid.toFixed(2)}</td></tr>
            ` : `
              <tr><td><strong>Total Expenses:</strong></td><td>₹${totalExpenses.toFixed(2)}</td></tr>
            `}
          </table>
        </div>
        <div class="footer">
          <p>This is a system generated report. No signature required.</p>
          <p>Printed on: ${new Date().toLocaleString('en-IN')}</p>
        </div>
        <div style="text-align:center; margin-top:20px;" class="no-print">
          <button onclick="window.print()" style="padding:10px 30px; background:#2E7D32; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px;">Print / Download PDF</button>
          <button onclick="window.close()" style="padding:10px 30px; background:#666; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px; margin-left:10px;">Close</button>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Finance Management</Typography>
        <Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { fetchAllData(); }} sx={{ mr: 1 }}>Refresh</Button>
          <Button variant="outlined" startIcon={<Print />} onClick={handlePrintReport}>Print Report</Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}><StatCard title="Total Received" value={totalReceived} color="#2E7D32" icon={<AttachMoney />} /></Grid>
        <Grid item xs={12} sm={4}><StatCard title="Total Paid" value={totalPaid} color="#D32F2F" icon={<AttachMoney />} /></Grid>
        <Grid item xs={12} sm={4}><StatCard title="Total Expenses" value={totalExpenses} color="#ED6C02" icon={<AttachMoney />} /></Grid>
      </Grid>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Payments" /><Tab label="Expenses" />
      </Tabs>

      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Payments</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setEditingPayment(null);
              setPaymentForm(emptyPaymentForm());
              setSelectedOrderDetails(null);
              setPaymentHistory([]);
              setOpenPaymentDialog(true);
            }}>Record Payment</Button>
          </Box>
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Party</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Settlement</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Transaction Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center">No payments recorded</TableCell></TableRow>
                  ) : (
                    payments.map((p) => {
                      let partyName = 'N/A';
                      if (p.customer && typeof p.customer === 'object') {
                        partyName = p.customer.name || 'N/A';
                      } else if (p.supplier && typeof p.supplier === 'object') {
                        partyName = p.supplier.name || 'N/A';
                      } else if (p.customer_name) {
                        partyName = p.customer_name;
                      } else if (p.supplier_name) {
                        partyName = p.supplier_name;
                      } else if (p.party_name) {
                        partyName = p.party_name;
                      }

                      let orderNumber = 'N/A';
                      if (p.sales_order && typeof p.sales_order === 'object') {
                        orderNumber = p.sales_order.so_number || 'N/A';
                      } else if (p.purchase_order && typeof p.purchase_order === 'object') {
                        orderNumber = p.purchase_order.po_number || 'N/A';
                      } else if (p.sales_order_number) {
                        orderNumber = p.sales_order_number;
                      } else if (p.purchase_order_number) {
                        orderNumber = p.purchase_order_number;
                      } else if (p.order_number) {
                        orderNumber = p.order_number;
                      }

                      return (
                        <TableRow key={p.id}>
                          <TableCell><Chip label={p.payment_type.toUpperCase()} color={p.payment_type === 'received' ? 'success' : 'error'} size="small" /></TableCell>
                          <TableCell>{partyName}</TableCell>
                          <TableCell>{orderNumber}</TableCell>
                          <TableCell><Chip label={SETTLEMENT_LABELS[p.settlement_type] || p.settlement_type} size="small" variant="outlined" /></TableCell>
                          <TableCell>₹{parseFloat(p.amount).toFixed(2)}</TableCell>
                          <TableCell>{p.payment_method?.replace('_', ' ').toUpperCase()}</TableCell>
                          <TableCell>{new Date(p.payment_date).toLocaleString('en-IN')}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditPaymentDialog(p)}><Edit /></IconButton>
                            <IconButton size="small" color="primary" onClick={() => printPaymentReceipt(p)}><Print /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handlePaymentDelete(p.id)}><Delete /></IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Expenses</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setEditingExpense(null);
              setExpenseForm(emptyExpenseForm());
              setOpenExpenseDialog(true);
            }}>Record Expense</Button>
          </Box>
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Tonnage</TableCell>
                    <TableCell align="right">Rate/Ton</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Transaction Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center">No expenses recorded</TableCell></TableRow>
                  ) : (
                    expenses.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{getCategoryChip(e.category)}</TableCell>
                        <TableCell>{e.description}</TableCell>
                        <TableCell align="right">₹{parseFloat(e.amount).toFixed(2)}</TableCell>
                        <TableCell>{e.tonnage ? `${e.tonnage} tons` : '-'}</TableCell>
                        <TableCell align="right">{e.rate_per_ton ? `₹${e.rate_per_ton}` : '-'}</TableCell>
                        <TableCell>{e.vendor || 'N/A'}</TableCell>
                        <TableCell>{new Date(e.expense_date).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditExpenseDialog(e)}><Edit /></IconButton>
                          <IconButton size="small" color="primary" onClick={() => printExpenseVoucher(e)}><Print /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleExpenseDelete(e.id)}><Delete /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{editingPayment ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Type</InputLabel>
                <Select value={paymentForm.payment_type} onChange={(e) => { setPaymentForm({ ...paymentForm, payment_type: e.target.value, customer: '', sales_order: '', supplier: '', purchase_order: '' }); setSelectedOrderDetails(null); setPaymentHistory([]); }} label="Payment Type">
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select value={paymentForm.payment_method} onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})} label="Payment Method">
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="online">Online Payment</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {paymentForm.payment_type === 'received' ? (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Customer</InputLabel>
                    <Select value={paymentForm.customer} onChange={(e) => handleCustomerChange(e.target.value)} label="Customer">
                      <MenuItem value="">Select Customer</MenuItem>
                      {customers.map((c) => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sales Order</InputLabel>
                    <Select value={paymentForm.sales_order} onChange={(e) => handleOrderSelect(e.target.value, 'sales')} label="Sales Order" disabled={!paymentForm.customer}>
                      <MenuItem value="">Select Order</MenuItem>
                      {salesOrders.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                          {o.so_number} - Due: ₹{(parseFloat(o.net_amount) - parseFloat(o.amount_paid || 0)).toFixed(2)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select value={paymentForm.supplier} onChange={(e) => handleSupplierChange(e.target.value)} label="Supplier">
                      <MenuItem value="">Select Supplier</MenuItem>
                      {suppliers.map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Purchase Order</InputLabel>
                    <Select value={paymentForm.purchase_order} onChange={(e) => handleOrderSelect(e.target.value, 'purchase')} label="Purchase Order" disabled={!paymentForm.supplier}>
                      <MenuItem value="">Select Order</MenuItem>
                      {purchaseOrders.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                          {o.po_number} - Due: ₹{(parseFloat(o.net_amount) - parseFloat(o.amount_paid || 0)).toFixed(2)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Settlement Type</InputLabel>
                <Select
                  value={paymentForm.settlement_type}
                  onChange={(e) => handleSettlementTypeChange(e.target.value)}
                  label="Settlement Type"
                >
                  <MenuItem value="full">Full Payment</MenuItem>
                  <MenuItem value="partial">Partial Payment</MenuItem>
                  <MenuItem value="final">Final Payment</MenuItem>
                  <MenuItem value="advance">Advance Payment</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Payment Date — NEW */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={paymentForm.payment_date || ''}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            {selectedOrderDetails && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Order Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><Typography variant="body2"><strong>Order No:</strong> {selectedOrderDetails.order_no}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="body2"><strong>Order Type:</strong> {selectedOrderDetails.order_type === 'sales' ? 'Sales Order' : 'Purchase Order'}</Typography></Grid>
                    <Grid item xs={4}><Typography variant="body2"><strong>Total Amount:</strong> ₹{selectedOrderDetails.total_amount.toFixed(2)}</Typography></Grid>
                    <Grid item xs={4}><Typography variant="body2"><strong>Amount Paid:</strong> ₹{selectedOrderDetails.amount_paid.toFixed(2)}</Typography></Grid>
                    <Grid item xs={4}><Typography variant="body2" style={{ color: selectedOrderDetails.due_amount > 0 ? '#d32f2f' : '#2e7d32' }}><strong>Due Amount:</strong> ₹{selectedOrderDetails.due_amount.toFixed(2)}</Typography></Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {paymentHistory.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="secondary" gutterBottom>
                    Payment History (Order: {selectedOrderDetails?.order_no})
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Transaction Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Settlement</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistory.map((h) => (
                        <TableRow key={h.id}>
                          <TableCell>{new Date(h.payment_date).toLocaleString('en-IN')}</TableCell>
                          <TableCell>₹{parseFloat(h.amount).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={SETTLEMENT_LABELS[h.settlement_type] || h.settlement_type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{h.payment_method?.replace('_', ' ').toUpperCase()}</TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={() => viewPaymentDetail(h)}
                              sx={{ fontSize: '10px' }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount (₹)"
                type="number"
                value={paymentForm.amount || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setPaymentForm({...paymentForm, amount: val === '' ? 0 : parseFloat(val) || 0});
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Reference Number" value={paymentForm.reference_number} onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
          <Button onClick={handlePaymentSubmit} variant="contained">{editingPayment ? 'Update' : 'Record'} Payment</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Detail Dialog */}
      <Dialog open={openPaymentDetailDialog} onClose={() => setOpenPaymentDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Payment Details</DialogTitle>
        <DialogContent>
          {selectedPaymentDetail && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Payment Information
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Payment Type:</strong></Typography>
                      <Chip
                        label={selectedPaymentDetail.payment_type?.toUpperCase() || 'N/A'}
                        color={selectedPaymentDetail.payment_type === 'received' ? 'success' : 'error'}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Amount:</strong></Typography>
                      <Typography variant="h6" color="#2E7D32">₹{parseFloat(selectedPaymentDetail.amount).toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Settlement Type:</strong></Typography>
                      <Chip
                        label={SETTLEMENT_LABELS[selectedPaymentDetail.settlement_type] || selectedPaymentDetail.settlement_type}
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Payment Method:</strong></Typography>
                      <Typography variant="body2">{selectedPaymentDetail.payment_method?.replace('_', ' ').toUpperCase() || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Transaction Date:</strong></Typography>
                      <Typography variant="body2">
                        {new Date(selectedPaymentDetail.payment_date).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Reference:</strong></Typography>
                      <Typography variant="body2">{selectedPaymentDetail.reference_number || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2"><strong>Party:</strong></Typography>
                      <Typography variant="body2">{selectedPaymentDetail.customer?.name || selectedPaymentDetail.supplier?.name || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Order Details
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Order No:</strong></Typography>
                      <Typography variant="body2">{selectedPaymentDetail.sales_order?.so_number || selectedPaymentDetail.purchase_order?.po_number || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Order Type:</strong></Typography>
                      <Typography variant="body2">{selectedPaymentDetail.is_sales_order ? 'Sales Order' : 'Purchase Order'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Order Value:</strong></Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ₹{parseFloat(selectedPaymentDetail.order_value || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Order Status:</strong></Typography>
                      <Chip
                        label={selectedPaymentDetail.order_status || 'N/A'}
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Net Amount:</strong></Typography>
                      <Typography variant="body2" fontWeight="bold" color="#2E7D32">
                        ₹{parseFloat(selectedPaymentDetail.net_amount || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Total Tax:</strong></Typography>
                      <Typography variant="body2" color="#D32F2F">
                        ₹{parseFloat(selectedPaymentDetail.tax_amount || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="secondary" gutterBottom>
                        <strong>Balance at the time of this payment</strong>
                        <Chip
                          label={new Date(selectedPaymentDetail.payment_date).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="body2"><strong>Cumulative Paid:</strong></Typography>
                          <Typography variant="body2" color="#1976D2" fontWeight="bold">
                            ₹{parseFloat(selectedPaymentDetail.cumulative_amount_paid || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2"><strong>This Payment:</strong></Typography>
                          <Typography variant="body2" color="#2E7D32" fontWeight="bold">
                            ₹{parseFloat(selectedPaymentDetail.amount || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2"><strong>Due Amount:</strong></Typography>
                          <Typography variant="body2" style={{
                            color: parseFloat(selectedPaymentDetail.running_due_amount || 0) > 0 ? '#d32f2f' : '#2e7d32',
                            fontWeight: 'bold'
                          }}>
                            ₹{parseFloat(selectedPaymentDetail.running_due_amount || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {(selectedPaymentDetail.gst_rate_details ||
                      parseFloat(selectedPaymentDetail.cgst_amount || 0) > 0 ||
                      parseFloat(selectedPaymentDetail.sgst_amount || 0) > 0 ||
                      parseFloat(selectedPaymentDetail.igst_amount || 0) > 0) && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="secondary" gutterBottom>
                          <strong>GST Details</strong>
                        </Typography>
                        <Grid container spacing={1}>
                          {selectedPaymentDetail.gst_rate_details && (
                            <Grid item xs={6}>
                              <Typography variant="body2"><strong>GST Rate:</strong></Typography>
                              <Typography variant="body2">
                                {selectedPaymentDetail.gst_rate_details?.name || 'N/A'}
                              </Typography>
                            </Grid>
                          )}
                          {parseFloat(selectedPaymentDetail.cgst_amount || 0) > 0 && (
                            <>
                              <Grid item xs={4}>
                                <Typography variant="body2"><strong>CGST:</strong></Typography>
                                <Typography variant="body2">
                                  ₹{parseFloat(selectedPaymentDetail.cgst_amount || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2"><strong>SGST:</strong></Typography>
                                <Typography variant="body2">
                                  ₹{parseFloat(selectedPaymentDetail.sgst_amount || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            </>
                          )}
                          {parseFloat(selectedPaymentDetail.igst_amount || 0) > 0 && (
                            <Grid item xs={4}>
                              <Typography variant="body2"><strong>IGST:</strong></Typography>
                              <Typography variant="body2">
                                ₹{parseFloat(selectedPaymentDetail.igst_amount || 0).toFixed(2)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {selectedPaymentDetail.notes && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                    <Typography variant="body2"><strong>Notes:</strong></Typography>
                    <Typography variant="body2">{selectedPaymentDetail.notes}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Print />}
            onClick={() => {
              if (selectedPaymentDetail) {
                printPaymentReceipt(selectedPaymentDetail);
              }
            }}
          >
            Print Receipt
          </Button>
          <Button onClick={() => setOpenPaymentDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={openExpenseDialog} onClose={() => setOpenExpenseDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{editingExpense ? 'Edit Expense' : 'Record Expense'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={expenseForm.category} onChange={(e) => handleCategoryChange(e.target.value)} label="Category">
                  <MenuItem value="electricity">Electricity Bill</MenuItem>
                  <MenuItem value="diesel">Diesel for Vehicles</MenuItem>
                  <MenuItem value="labour">Labour Wages</MenuItem>
                  <MenuItem value="driver_salary">Driver Salary</MenuItem>
                  <MenuItem value="vehicle_maintenance">Vehicle Maintenance</MenuItem>
                  <MenuItem value="machine_maintenance">Machine Maintenance</MenuItem>
                  <MenuItem value="machine_parts">Machine Spare Parts</MenuItem>
                  <Divider />
                  <MenuItem value="loading">🚛 Loading Charges</MenuItem>
                  <MenuItem value="unloading">🚛 Unloading Charges</MenuItem>
                  <Divider />
                  <MenuItem value="others">Others</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Expense Date — NEW */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expense Date"
                type="date"
                value={expenseForm.expense_date || ''}
                onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Amount (₹)" type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({...expenseForm, amount: parseFloat(e.target.value)})} disabled={isHandlingCategory(expenseForm.category)} helperText={isHandlingCategory(expenseForm.category) ? 'Auto-calculated from tonnage × rate' : ''} />
            </Grid>
            {isHandlingCategory(expenseForm.category) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    <Calculate sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Handling Charges Calculation
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Tonnage (Tons)" type="number" value={expenseForm.tonnage} onChange={(e) => setExpenseForm({...expenseForm, tonnage: e.target.value})} inputProps={{ step: 0.01, min: 0 }} helperText="Enter total tons handled" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Rate per Ton (₹)" type="number" value={expenseForm.rate_per_ton} onChange={(e) => setExpenseForm({...expenseForm, rate_per_ton: e.target.value})} inputProps={{ step: 0.01, min: 0 }} helperText="Enter rate per ton" />
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="body2">
                      <strong>Calculation:</strong> {parseFloat(expenseForm.tonnage) || 0} tons × ₹{parseFloat(expenseForm.rate_per_ton) || 0} = <strong style={{ color: '#2E7D32' }}>₹{(parseFloat(expenseForm.tonnage) || 0) * (parseFloat(expenseForm.rate_per_ton) || 0)}</strong>
                    </Typography>
                  </Paper>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={expenseForm.description} onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})} multiline rows={2} required helperText={isHandlingCategory(expenseForm.category) ? 'e.g., Loading of raw shells' : 'Enter expense description'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Vendor" value={expenseForm.vendor} onChange={(e) => setExpenseForm({...expenseForm, vendor: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Bill Number" value={expenseForm.bill_number} onChange={(e) => setExpenseForm({...expenseForm, bill_number: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExpenseDialog(false)}>Cancel</Button>
          <Button onClick={handleExpenseSubmit} variant="contained">{editingExpense ? 'Update' : 'Record'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Finance;